import React, { useEffect, useRef, useState } from 'react'
import './functions.styles.scss';
import { UseCloudAdminFn, UseCloudFunction } from '@/utils/firebase.utils'
import Modal, { ModalAlert } from '@/components/modal/modal.component';
import packageData from '@/utils/free-icons-to-upload.json';
import { set } from 'date-fns';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { getQueryFromFirestore } from '@/utils/getDataFromDbSimplified';

const AdminFunctions = () => {

  const [showCopyCollectionModal, setShowCopyCollectionModal] = useState(false);
  const [showUpdatePlayerDocsModal, setShowUpdatePlayerDocsModal] = useState(false);
  const [showGetGameId, setShowGetGameId] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [showNewAdminModal, setShowNewAdminModal] = useState(false);
  const [showContactBackfillModal, setShowContactBackfillModal] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [ emails, setEmails] = useState([]);
  const approveRef = useRef();

  // the backend works to a time budget and hands back a cursor when it stops
  // early, so a single click keeps calling until the run reports complete
  const MAX_BACKFILL_PASSES = 50;

  useEffect(() => {
    changeDocumentTitle('Gamably Admin | Functions');
  }, [])

  async function testRtDb() {
    setShowGetGameId(true);
    approveRef.current = async () => {
      const gameId = document.getElementById('getGameId').value;
      const res = await UseCloudFunction(
        'testRtDatabaseQuery',
        {
          'gameId': gameId
        }
      )
      console.log(res);
    }
  }

  async function updateActivity() {
    const res = await UseCloudFunction('updateActivity', { 'obj': 'data' });
    console.log(res);
  }


  async function updateActivityAndGoals() {
    const res = await UseCloudFunction('updateActivityAndGoals', { 'obj': 'data' });
    console.log(res);
  }

  async function copyFirestoreCollection() {
    const existingName = document.getElementById('existing-name').value;
    const newName = document.getElementById('new-name').value;
    const res = await UseCloudAdminFn(
      'copyFirestoreCollection',
      {
        'existingName': existingName,
        'newName': newName
      }
    )
    console.log(res);
  }

  function cancelCopyFirestoreCollection() {
    const els = document.getElementsByClassName('copy-firestore-input')
    for (let el of els) {
      el.value = '';
    }
    setShowCopyCollectionModal(false)
  }

  async function moveGameElements() {
    const res = await UseCloudFunction('moveGameElements', {})
    console.log(res);
  }
  async function moveGameActivity() {
    const res = await UseCloudFunction('moveGameActivity', {})
    console.log(res);
  }

  async function updatePlayerDocs() {
    const gameId = document.getElementById('gameId').value;
    if (!gameId) return;
    const res = await UseCloudFunction(
      'updatePlayerDocs',
      {
        'gameId': gameId
      }
    )
    console.log(res);
    cancelUpdatePlayerDocs();
  }

  function cancelUpdatePlayerDocs() {
    document.getElementById('gameId').value = ''
    setShowUpdatePlayerDocsModal(false);
  }

  async function runCloudFunction(fnName) {
    const res = await UseCloudFunction(fnName, {});
    console.log(res);
  }

  async function updateGameLotteries() {
    setShowGetGameId(true);
    approveRef.current = async () => {
      const gameId = document.getElementById('getGameId').value;
      const res = await UseCloudFunction(
        'updatePlayerLotteryCount',
        {
          'gameId': gameId
        }
      );
      console.log(res);
      document.getElementById('getGameId').value = null;
      setShowGetGameId(false);
    }
  }

  async function combineGameData(event) {
    const gameId = document.getElementById('getGameId').value;
    const res = await UseCloudAdminFn(
      'combineGameDataInFirestore',
      {
        'gameId': gameId
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage(`Something went wrong: ${res.error}`);
      return;
    }
    setAlertMessage(`Game data combined successfully for gameId: ${gameId}`);
    document.getElementById('getGameId').value = null;
    setShowGetGameId(false);
  }

  async function moveLeaderboardsToSubcollection() {
    const res = await UseCloudAdminFn(
      'moveLeaderboardsToSubcollection',
      {}
    )
    console.log(res);
  }

  async function getCreatedGameCounts() {
    const res = await UseCloudAdminFn(
      'getCreatedGameCounts',
      {}
    )
    console.log(res);
  }

  async function createUserProfilesInRtDb() {
    const res = await UseCloudAdminFn(
      'createUserProfilesInRtDb',
      {}
    )
    console.log(res);
  }

  async function createNewPlatformAdmin() {
    const res = await UseCloudAdminFn(
      'createPlatformAdminUser',
      { 'appId': document.getElementById('adminAppId').value }
    )
    console.log(res);
    document.getElementById('adminAppId').value = null;
    setShowNewAdminModal(false);
  }

  /**
   * Runs the email contact backfill, calling the cloud function repeatedly
   * until it reports complete. Each pass stops before the callable's timeout
   * and returns a cursor to resume from.
   */
  async function runContactBackfill(dryRun) {
    setBackfillRunning(true);
    const totals = {
      scanned: 0,
      eligible: 0,
      sent: 0,
      failed: 0,
      tsCreatedAdded: 0,
      skippedNoTimestamps: 0,
      skippedNoEmail: 0,
      skippedUnderAge: 0,
      withGamesCreated: 0,
      withOrgJoinDates: 0,
      withCreatorFlag: 0,
    };
    let cursor = null;
    let passes = 0;
    let done = false;
    let failure = null;

    try {
      while (!done) {
        const res = await UseCloudAdminFn(
          'createEmailContactsForExistingUsers',
          { dryRun: dryRun, startAfter: cursor }
        );
        passes++;

        if (res.counts) {
          // the function returns partial counts alongside an error
          for (const key of Object.keys(totals)) {
            totals[key] += res.counts[key] || 0;
          }
        } else {
          for (const key of Object.keys(totals)) {
            totals[key] += res[key] || 0;
          }
        }

        if (res.error) {
          failure = res.error;
          break;
        }

        setBackfillProgress({ ...totals, passes, complete: res.complete });

        if (res.complete) {
          done = true;
          break;
        }

        const nextCursor = res.nextCursor;
        if (!nextCursor) {
          failure = 'Run stopped early without a cursor to resume from.';
          break;
        }
        // a cursor that has not moved would loop forever
        if (
          cursor &&
          cursor.birthday === nextCursor.birthday &&
          cursor.appId === nextCursor.appId
        ) {
          failure = 'Cursor stopped advancing — halted to avoid a loop.';
          break;
        }
        cursor = nextCursor;

        if (passes >= MAX_BACKFILL_PASSES) {
          failure = `Stopped after ${MAX_BACKFILL_PASSES} passes. Run again to continue.`;
          break;
        }
      }
    } catch (err) {
      failure = err.message;
    }

    setBackfillProgress({ ...totals, passes, complete: done });
    setBackfillRunning(false);

    if (failure) {
      setAlertMessage(`Backfill stopped: ${failure}`);
      return;
    }
    setAlertMessage(
      dryRun
        ? `Dry run complete. ${totals.eligible} contact${totals.eligible === 1 ? '' : 's'} would be sent from ${totals.scanned} profile${totals.scanned === 1 ? '' : 's'} scanned. Nothing was written.`
        : `Backfill complete. ${totals.sent} contact${totals.sent === 1 ? '' : 's'} sent from ${totals.scanned} profile${totals.scanned === 1 ? '' : 's'} scanned.`
    );
  }

  /**
   * Moves answer keys and loot key codes off the player-readable gameElements
   * docs into the server-only privateElementData store. Like the contact
   * backfill, each pass stops before the callable's timeout and hands back a
   * cursor; this keeps calling until the server reports done.
   */
  async function runPrivateDataMigration(dryRun) {
    setMigrationRunning(true);
    const totals = { gamesChecked: 0, elementsMoved: 0, challenge: 0, quest: 0, lootKey: 0 };
    let cursor = null;
    let passes = 0;
    let done = false;
    let failure = null;

    try {
      while (!done) {
        const res = await UseCloudAdminFn(
          'migratePrivateElementData',
          { dryRun: dryRun, startAfterGameId: cursor }
        );
        passes++;
        if (res.error) {
          failure = res.error;
          break;
        }
        totals.gamesChecked += res.gamesChecked || 0;
        totals.elementsMoved += res.elementsMoved || 0;
        for (const type of ['challenge', 'quest', 'lootKey']) {
          totals[type] += res.byType?.[type] || 0;
        }
        setMigrationProgress({ ...totals, passes, dryRun, complete: !!res.done });

        if (res.done) {
          done = true;
          break;
        }
        const nextCursor = res.nextStartAfterGameId;
        if (!nextCursor) {
          failure = 'Run stopped early without a cursor to resume from.';
          break;
        }
        // a cursor that has not moved would loop forever
        if (nextCursor === cursor) {
          failure = 'Cursor stopped advancing — halted to avoid a loop.';
          break;
        }
        cursor = nextCursor;

        if (passes >= MAX_BACKFILL_PASSES) {
          failure = `Stopped after ${MAX_BACKFILL_PASSES} passes. Run again to continue — it picks up where it left off.`;
          break;
        }
      }
    } catch (err) {
      failure = err.message;
    }

    setMigrationProgress({ ...totals, passes, dryRun, complete: done });
    setMigrationRunning(false);

    if (failure) {
      setAlertMessage(`Migration stopped: ${failure}`);
      return;
    }
    const count = `${totals.elementsMoved} element${totals.elementsMoved === 1 ? '' : 's'}`;
    const games = `${totals.gamesChecked} game${totals.gamesChecked === 1 ? '' : 's'}`;
    setAlertMessage(
      dryRun
        ? `Dry run complete. ${count} across ${games} still hold private data and would be moved. Nothing was written.`
        : `Migration complete. Private data moved for ${count} across ${games}.`
    );
  }

  async function runAdminCloudFunction(fnName, data) {
    const res = await UseCloudAdminFn(fnName, data ?? {});
    console.log(res);
    if (res.error) {
      setAlertMessage(`Something went wrong: ${res.error}`);
      return;
    }
    setAlertMessage(`Cloud function ${fnName} executed successfully.`);
  }

  async function displayUsersOver18() {
    const age18Date = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).getTime();
    console.log(age18Date);
    getQueryFromFirestore({
      'path': 'users',
      'queryArray': [{
        'field': 'birthday', 
        'operator': '<=',
        'value': age18Date
      }],
      'once': true,
      'callback': callback
    })
    function callback(data) {
      const emails = Object.values(data).map(user => user.email);
      setEmails(emails.sort());
    }
  }

  return (
    <div className='functions-container'>
      {/* <div>
                <button 
                    className='g-button' 
                    onClick={() => testRtDb()}
                >
                    Test Realtime DB
                </button>
            </div>
            <div>
                <button 
                    className='g-button' 
                    onClick={() => runCloudFunction('moveGameElementsToRtDb')}
                >
                    Move Game Elements to RtDb
                </button>
            </div>
            <div>
                <button 
                    className='g-button' 
                    onClick={() => updateGameLotteries()}
                >
                    Update Game Lotteries
                </button>
            </div> */}
      {/* <div>
                <button 
                    className='g-button' 
                    onClick={() => updateActivityAndGoals()}
                >
                    Update Activity and Goals for multiple elements
                </button>
            </div>
            <div>
                <button 
                    className='g-button' 
                    onClick={() => updateActivity()}
                >
                    Update Activity Sort Titles
                </button>
            </div> */}
      <div>
        <button
          className='g-button'
          onClick={() => setShowCopyCollectionModal(true)}
        >
          Copy a Firestore Collection
        </button>
      </div>

      {/* <div>
                <button 
                    className='g-button' 
                    onClick={() => moveLeaderboardsToSubcollection()}
                >
                    Move Leaderboards to Subcollection
                </button>
            </div> */}

      {/* <div>
                <button 
                    className='g-button'
                    onClick={() => {
                        approveRef.current = combineGameData;
                        setShowGetGameId(true);
                    }}>
                    Reorganize Game Data (7/2025)
                </button>
            </div> */}

      <div>
        <button
          className='g-button'
          onClick={() => createUserProfilesInRtDb()}
        >
          Create User Profiles in Realtime Database
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => getCreatedGameCounts()}
        >
          Get Created Game Counts
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => setShowNewAdminModal(true)}
        >
          Create Platform Admin User
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => runAdminCloudFunction('moveContentPackagesToFirestore')}>
          Move Content Packages to Firestore
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => runAdminCloudFunction(
            'testBrevoEmail',
            {
              'email': 'adam@tamingthetech.org'
            }
          )}>
          Test Brevo Email
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => runAdminCloudFunction('combineGameIndex')}
        >
          Combine Game Index
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => {
            setBackfillProgress(null);
            setShowContactBackfillModal(true);
          }}
        >
          Create Email Contacts for Existing Users
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => {
            setMigrationProgress(null);
            setShowMigrationModal(true);
          }}
        >
          Migrate Private Element Data
        </button>
      </div>
      <div>
        <button
          className='g-button'
          onClick={() => displayUsersOver18()}
        >
          Display Users over 18
        </button>

      </div>
      {/* <div>
                <button
                    className='g-button' 
                    onClick={() => moveGameElements()}
                >
                    Move Game Elements
                </button>
            </div>
            <div>
                <button 
                    className='g-button' 
                    onClick={() => moveGameActivity()}
                >
                    Move Game Activity
                </button>
            </div>
            <div>
                <button 
                    className='g-button' 
                    onClick={() => runCloudFunction('addOptAvailable')}
                >
                    Add Opt_Available to Game Elements
                </button>
            </div>
            <div>
                <button
                    className='g-button'
                    onClick={() => setShowUpdatePlayerDocsModal(true)}
                >
                    Update Player Docs
                </button>
            </div> */}
      {/* <div>
                <button className='g-button' onClick={() => createPlayerPaths()}>Create Player Paths</button>
            </div> */}
      <Modal
        show={showCopyCollectionModal}
        cancel={() => setShowCopyCollectionModal(false)}
        text='Copy a Firestore Collection'
        closeButton={true}
        cls='narrow'
      >
        <div className='modal-children copy-firestore-collection-modal'>
          <div className='field'>
            <label>Existing Collection Name</label>
            <input type='text' id='existing-name' />
          </div>
          <div className='field'>
            <label>New Collection Name</label>
            <input type='text' id='new-name' />
          </div>
          <div className='buttons'>
            <button className='g-button' onClick={() => cancelCopyFirestoreCollection()}>Cancel</button>
            <button className='g-button' onClick={() => copyFirestoreCollection()}>Copy Collection</button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showUpdatePlayerDocsModal}
        cancel={() => setShowUpdatePlayerDocsModal(false)}
        text='Update Player Docs'
        closeButton={true}
        cls='narrow'
      >
        <div className='modal-children copy-firestore-collection-modal'>
          <div className='field'>
            <label>Game Id</label>
            <input type='text' id='gameId' />
          </div>
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button className='g-button' onClick={() => cancelUpdatePlayerDocs()}>Cancel</button>
            <button className='g-button' onClick={() => updatePlayerDocs()}>Update Player Docs</button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showGetGameId}
        cancel={() => setShowGetGameId(false)}
        text='Get Game Id'
        closeButton={true}
        cls='narrow'
      >
        <div className='modal-children copy-firestore-collection-modal'>
          <div className='field'>
            <label>Game Id</label>
            <input type='text' id='getGameId' />
          </div>
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button className='g-button' onClick={() => {
              setShowGetGameId(false);
              document.getElementById('getGameId').value = null;
            }}>Cancel</button>
            <button
              className='g-button'
              onClick={approveRef.current}>
              Use Game Id
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showNewAdminModal}
        cancel={() => setShowNewAdminModal(false)}
        text='Create Platform Admin User'
        closeButton={true}
        cls='narrow'>
        <div className='modal-children copy-firestore-collection-modal'>
          <div className='field'>
            <label>Admin AppId</label>
            <input type='text' id='adminAppId' />
          </div>
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button className='g-button' onClick={() => setShowNewAdminModal(false)}>Cancel</button>
            <button
              className='g-button'
              onClick={createNewPlatformAdmin}>
              Create Admin User
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showContactBackfillModal}
        cancel={() => {
          if (backfillRunning) return;
          setShowContactBackfillModal(false);
        }}
        text='Create Email Contacts for Existing Users'
        closeButton={!backfillRunning}
        cls='narrow'>
        <div className='modal-children'>
          <p>
            Creates a contact in the email provider for every existing user
            profile, and adds them to the new-user list.
          </p>
          <ul>
            <li>Profiles with no birthday are skipped — age cannot be confirmed.</li>
            <li>Anyone under 18 is skipped.</li>
            <li>Profiles with no join date at all are skipped.</li>
            <li>
              Profiles with a join date but no created date have the created
              date filled in from it.
            </li>
          </ul>
          <p>
            Run the dry run first. It reports the same counts without sending
            anything or writing to any profile.
          </p>
          {
            backfillProgress &&
            <div className='field'>
              <label>{backfillRunning ? 'Running…' : 'Last run'}</label>
              <ul>
                <li>Profiles scanned: {backfillProgress.scanned}</li>
                <li>
                  Contacts {backfillProgress.sent ? 'sent' : 'eligible'}:{' '}
                  {backfillProgress.sent || backfillProgress.eligible}
                </li>
                <li>Failed to send: {backfillProgress.failed}</li>
                <li>Created dates filled in: {backfillProgress.tsCreatedAdded}</li>
                <li>Skipped, no join date: {backfillProgress.skippedNoTimestamps}</li>
                <li>Skipped, no email: {backfillProgress.skippedNoEmail}</li>
                <li>Skipped, under 18: {backfillProgress.skippedUnderAge}</li>
                <li>Passes: {backfillProgress.passes}</li>
              </ul>
              <label>Creator flag (HAS_CREATED_GAME)</label>
              <ul>
                <li>Profiles with games created: {backfillProgress.withGamesCreated}</li>
                <li>Profiles with org join dates: {backfillProgress.withOrgJoinDates}</li>
                <li>Marked as creators: {backfillProgress.withCreatorFlag}</li>
              </ul>
            </div>
          }
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button
              className='g-button'
              disabled={backfillRunning}
              onClick={() => setShowContactBackfillModal(false)}>
              Cancel
            </button>
            <button
              className='g-button'
              disabled={backfillRunning}
              onClick={() => runContactBackfill(true)}>
              {backfillRunning ? 'Working…' : 'Dry Run'}
            </button>
            <button
              className='g-button'
              disabled={backfillRunning}
              onClick={() => runContactBackfill(false)}>
              {backfillRunning ? 'Working…' : 'Create Contacts'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={showMigrationModal}
        cancel={() => {
          if (migrationRunning) return;
          setShowMigrationModal(false);
        }}
        text='Migrate Private Element Data'
        closeButton={!migrationRunning}
        cls='narrow'>
        <div className='modal-children'>
          <p>
            Moves secrets off the game element docs, which every player can
            read, into a store only Cloud Functions can reach:
          </p>
          <ul>
            <li>Challenge answer keys (short-answer answers, correct multiple-choice option)</li>
            <li>Quest checkpoint answer keys</li>
            <li>Loot key codes and their claim records</li>
          </ul>
          <ul>
            <li>Safe to run more than once — elements already moved are skipped.</li>
            <li>A value already in the private store (from a save since the update) is never overwritten.</li>
            <li>Uploaded-player passcodes are not part of this: the security rules already lock them down where they are.</li>
          </ul>
          <p>
            Run the dry run first. It reports the same counts without writing anything.
          </p>
          {
            migrationProgress &&
            <div className='field'>
              <label>
                {migrationRunning ? 'Running…' : `Last run${migrationProgress.dryRun ? ' (dry run)' : ''}`}
              </label>
              <ul>
                <li>Games checked: {migrationProgress.gamesChecked}</li>
                <li>
                  Elements {migrationProgress.dryRun ? 'to move' : 'moved'}: {migrationProgress.elementsMoved}
                </li>
                <li>Challenges: {migrationProgress.challenge}</li>
                <li>Quests: {migrationProgress.quest}</li>
                <li>Loot keys: {migrationProgress.lootKey}</li>
                <li>Passes: {migrationProgress.passes}</li>
              </ul>
            </div>
          }
          <div className='g-space-1'></div>
          <div className='buttons'>
            <button
              className='g-button'
              disabled={migrationRunning}
              onClick={() => setShowMigrationModal(false)}>
              Cancel
            </button>
            <button
              className='g-button'
              disabled={migrationRunning}
              onClick={() => runPrivateDataMigration(true)}>
              {migrationRunning ? 'Working…' : 'Dry Run'}
            </button>
            <button
              className='g-button'
              disabled={migrationRunning}
              onClick={() => runPrivateDataMigration(false)}>
              {migrationRunning ? 'Working…' : 'Migrate'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        show={emails.length > 0}
        cancel={() => setEmails([])}
        text="Emails of Users Under 18"
        cls='scroll'
        closeButton={true}
      >
        <ul>
          {emails.map((email, index) => (
            <li key={index}>{email}</li>
          ))}
        </ul>
      </Modal>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
    </div>
  )
}

export default AdminFunctions